import {AssetOptionKeyEnum} from './asset-option-key-enum'

// Column names in the OT Asset Excel template.
// Any reference to the column should use this enum.
export enum OTAssetKeys {
    NAME = 'Asset Name',
    TYPE = 'Type',
    VESSEL_SYSTEM = 'Vessel System',
    ZONE = 'Zone',
    BRAND = 'Brand/Manufacturer',
    MODEL = 'Model',
    DESCRIPTION = 'Short description of functionality/purpose',
    PHYSICAL_INTERFACES = 'Physical interfaces',
    COMMUNICATION_PROTOCOL = 'Supported communication protocols',
    SOFTWARE_TYPE = 'Name/type of system software',
    SOFTWARE_VERSION = 'Version and patch level of system software',
    STATUS = 'Lifecycle Status',
    IP_ADDRESS = 'IP Address',
    MAC_ADDRESS = 'MAC Address',
}

export enum OTAssetSpecialKeys {
    VESSEL_IMO = 'IMO Number',
    VESSEL_CODE = 'Vessel Code',
    NO = 'No',
    CUSTOM_FIELDS = 'Custom Fields',
}

// Keys that are meant to be used to
// reliably process the data. Not meant
// to be in the final output for the API
// or for users to see
export enum OTAssetInternalKeys {
    IDENTIFIER = '___identifier___',
}

export const OTAssetRequiredKeys = [OTAssetKeys.VESSEL_SYSTEM] as const

const OTAssetEnumKeys = [
    OTAssetKeys.SOFTWARE_TYPE,
    OTAssetKeys.ZONE,
    OTAssetKeys.VESSEL_SYSTEM,
    OTAssetKeys.PHYSICAL_INTERFACES,
    OTAssetKeys.COMMUNICATION_PROTOCOL,
    OTAssetKeys.TYPE,
    OTAssetKeys.STATUS,
] as const

const OTAssetEnumMultiValueKeys = [OTAssetKeys.PHYSICAL_INTERFACES] as const

export function isInternalOTAssetKey(value: string): value is OTAssetInternalKeys {
    for (const col of Object.values(OTAssetInternalKeys)) {
        const colValue: string = col
        if (colValue === value) {
            return true
        }
    }

    return false
}

export type OTAssetKeyType = `${OTAssetKeys}`
type OTAssetRequiredKey = (typeof OTAssetRequiredKeys)[number]
type OTAssetEnumKey = (typeof OTAssetEnumKeys)[number]
type OTAssetEnumMultiValueKey = (typeof OTAssetEnumMultiValueKeys)[number]

export function isOTAssetEnumKey(value: string): value is OTAssetEnumKey {
    return OTAssetEnumKeys.includes(value as OTAssetEnumKey)
}

export function isOTAssetEnumKeySupportingMultipleValues(value: string): value is OTAssetEnumKey {
    return OTAssetEnumMultiValueKeys.includes(value as OTAssetEnumMultiValueKey)
}

export function isOTAssetRequiredKey(value: string): value is OTAssetRequiredKey {
    return OTAssetRequiredKeys.includes(value as OTAssetRequiredKey)
}

export function isOTAssetKey(value: string): value is OTAssetKeys {
    for (const col of Object.values(OTAssetKeys)) {
        const columnValue: string = col
        if (columnValue === value) {
            return true
        }
    }

    return false
}

export function normaliseString(value: string | undefined): string | undefined {
    return value?.trim().toLowerCase()
}

export function isEquivalentOTString(a: string | undefined, b: string | undefined): boolean {
    if (a === undefined || b === undefined) {
        return false
    }
    return normaliseString(a) === normaliseString(b)
}

export const OTAssetKeyToAssetOptionKeyMap: Record<OTAssetEnumKey, AssetOptionKeyEnum> = {
    [OTAssetKeys.SOFTWARE_TYPE]: AssetOptionKeyEnum.SOFTWARE,
    [OTAssetKeys.COMMUNICATION_PROTOCOL]: AssetOptionKeyEnum.COMMUNICATION_PROTOCOL,
    [OTAssetKeys.ZONE]: AssetOptionKeyEnum.ZONE,
    [OTAssetKeys.PHYSICAL_INTERFACES]: AssetOptionKeyEnum.PHYSICAL_INTERFACE,
    [OTAssetKeys.VESSEL_SYSTEM]: AssetOptionKeyEnum.SYSTEM,
    [OTAssetKeys.TYPE]: AssetOptionKeyEnum.ASSET_TYPE,
    [OTAssetKeys.STATUS]: AssetOptionKeyEnum.LIFECYCLE_STATUS,
}

// Individual row from OT asset excel template.
// If the type for 1 of the row is different later on,
// we will need to adjust this to either specify each column indivdually
// or use some ternary operator to define the type like `K extends OTAssetKeys.IMO_NUMBER ? number : string;`
// The & will allow custom fields
export type OTAssetRow = {
    [K in OTAssetKeys]?: string
} & {
    [key: string]: string
} & {
    [k in OTAssetSpecialKeys]?: string
}
